import { Button, useI18n } from '@upweo/ui';
import type { AppCreatedResponse, AppScope, AppStatus } from '@upweo/types';
import { APP_SCOPES } from '@upweo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';
import { usePartner } from '../lib/partner-context.js';

const STATUS_STYLES: Record<AppStatus, string> = {
  APPROVED: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  PENDING_REVIEW: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  DRAFT: 'bg-muted text-muted-foreground',
  REJECTED: 'bg-destructive/10 text-destructive',
  SUSPENDED: 'bg-destructive/10 text-destructive',
};

/**
 * Shown once, immediately after creation.
 *
 * The server stores only an argon2id digest of the secret, so there is no
 * endpoint that could ever return it again — the copy-now warning is literal,
 * not a nudge.
 */
const SecretPanel = ({
  app,
  onDismiss,
}: {
  app: AppCreatedResponse;
  onDismiss: () => void;
}): React.JSX.Element => {
  const { t } = useI18n();

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-5">
      <h2 className="font-medium">{app.name} created</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Copy the client secret now. It is stored only as a hash — this is the one and only time it
        can be shown.
      </p>
      <dl className="mt-4 space-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('partner.clientId')}
          </dt>
          <dd className="mt-0.5 break-all font-mono text-xs">{app.clientId}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('partner.clientSecret')}
          </dt>
          <dd className="mt-0.5 break-all rounded bg-background p-2 font-mono text-xs">
            {app.clientSecret}
          </dd>
        </div>
      </dl>
      <Button variant="outline" className="mt-4" onClick={onDismiss}>
        I&apos;ve copied it
      </Button>
    </div>
  );
};

const AppForm = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (app: AppCreatedResponse) => void;
}): React.JSX.Element => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [appUrl, setAppUrl] = useState('');
  const [scopes, setScopes] = useState<AppScope[]>(['read_products']);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createApp({
        name,
        scopes,
        redirectUris: appUrl ? [`${appUrl.replace(/\/+$/, '')}/auth/callback`] : [],
        distribution: 'PUBLIC',
        isEmbedded: true,
        ...(appUrl ? { appUrl } : {}),
      }),
    onSuccess: onCreated,
    onError: (err: unknown) => {
      setError(
        err instanceof ApiClientError
          ? (err.error.fieldErrors?.map((f) => `${f.path}: ${f.message}`).join(', ') ??
              err.error.message)
          : 'Could not create the app',
      );
    },
  });

  const field =
    'h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-lg border bg-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">{t('partner.newApp')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="app-name" className="text-sm font-medium">
            Name
          </label>
          <input
            id="app-name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="Inventory Sync"
            className={field}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="app-url" className="text-sm font-medium">
            App URL <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="app-url"
            type="url"
            value={appUrl}
            onChange={(e) => {
              setAppUrl(e.target.value);
            }}
            placeholder="https://app.example.com"
            className={field}
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('partner.requestedScopes')}</legend>
        <p className="text-xs text-muted-foreground">
          What the app may ask a merchant for. The store decides what to actually grant at install.
        </p>
        <div className="grid gap-1.5 sm:grid-cols-3">
          {APP_SCOPES.map((scope) => (
            <label key={scope} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={(e) => {
                  setScopes((prev) =>
                    e.target.checked ? [...prev, scope] : prev.filter((s) => s !== scope),
                  );
                }}
                className="h-3.5 w-3.5"
              />
              <span className="font-mono text-xs">{scope}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Creating…' : 'Create app'}
      </Button>
    </form>
  );
};

export const AppsPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeAccount } = usePartner();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<AppCreatedResponse | null>(null);

  const appsQuery = useQuery({
    queryKey: ['apps', activeAccount?.id],
    queryFn: () => api.listApps(),
    enabled: Boolean(activeAccount),
  });

  const submit = useMutation({
    mutationFn: (id: string) => api.updateApp(id, { submitForReview: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  const apps = appsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('partner.apps')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {activeAccount ? `${activeAccount.name} · ${apps.length} apps` : 'No partner account'}
          </p>
        </div>
        <Button
          onClick={() => {
            setCreating(true);
            setCreated(null);
          }}
        >
          Create app
        </Button>
      </div>

      {created && (
        <SecretPanel
          app={created}
          onDismiss={() => {
            setCreated(null);
          }}
        />
      )}

      {creating && (
        <AppForm
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(app) => {
            setCreating(false);
            setCreated(app);
            void queryClient.invalidateQueries({ queryKey: ['apps'] });
          }}
        />
      )}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-2.5 font-medium">App</th>
              <th className="px-4 py-2.5 font-medium">{t('partner.clientId')}</th>
              <th className="px-4 py-2.5 font-medium">{t('partner.scopes')}</th>
              <th className="px-4 py-2.5 font-medium">Installs</th>
              <th className="px-4 py-2.5 font-medium">{t('common.status')}</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {appsQuery.isPending && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {appsQuery.isError && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-destructive">
                  Failed to load apps
                </td>
              </tr>
            )}
            {!appsQuery.isPending && !appsQuery.isError && apps.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No apps yet.
                </td>
              </tr>
            )}
            {apps.map((app) => (
              <tr key={app.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3">
                  <div className="font-medium">{app.name}</div>
                  <div className="text-xs text-muted-foreground">/{app.handle}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{app.clientId}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {app.scopes.length} requested
                </td>
                <td className="px-4 py-3">{app.installCount}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[app.status]}`}
                  >
                    {app.status.toLowerCase().replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {app.status === 'DRAFT' && (
                    <button
                      type="button"
                      onClick={() => {
                        submit.mutate(app.id);
                      }}
                      className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Submit for review
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
